import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Activity, ChevronDown, ChevronRight, Play, DollarSign, Target, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useApi } from '../hooks/useApi';

const V5_PROBABILISTIC_MODEL = 'v5.0.0';
const V5_DEFAULTS = {
  fix_i_model_spread: 5,
  base_volume: 50,
  fix_i_model: 'v2.0.0',
  fix_ii_model: 'v1.0.0-fix2',
  ida1_model: 'ida1-v1.0.3',
  bucket1_volume: 20,
  bucket2_volume: 40,
  bucket3_volume: 60,
  fix_ii_ida1_bucket1_volume: 20,
  fix_ii_ida1_bucket2_volume: 40,
  fix_ii_ida1_bucket3_volume: 60,
  ida1_margin: 2500,
  preds_diff_cutoff: 5,
  prediction_filter_margin: 1000,
  max_avg_volume_pct: 50,
  max_position: 120,
  margin_tier1: 200,
  margin_tier2: 300,
  margin_tier3: 400,
  margin_tier4: 500,
  apply_ridge_shift: false
};

const IDA1Model = () => {
  const { fetchPost } = useApi();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate dates: 2 weeks back for start_date, tomorrow for end_date
  const getDefaultDates = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    return {
      start: formatDate(twoWeeksAgo),
      end: formatDate(tomorrow)
    };
  };

  const defaultDates = getDefaultDates();

  const [formData, setFormData] = useState({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
    fix_i_model_spread: 5,
    base_volume: 50,
    fix_i_model: 'v2.0.0',
    fix_ii_model: 'v1.0.0-fix2',
    probabilistic_model: 'v3.1.0',
    ida1_model: 'ida1-v1.0.3',
    ida1_margin: 1000,
    preds_diff_cutoff: 10,
    bucket1_volume: 20,
    bucket2_volume: 40,
    bucket3_volume: 60,
    prediction_filter_margin: 10,
    np_constraint_bucket1: 0,
    np_constraint_bucket2: 0,
    np_constraint_bucket3: 0,
    fix_ii_ida1_bucket1_volume: 20,
    fix_ii_ida1_bucket2_volume: 40,
    fix_ii_ida1_bucket3_volume: 60,
    max_avg_volume_pct: 30,
    max_position: 60,
    margin_tier1: 200,
    margin_tier2: 300,
    margin_tier3: 400,
    margin_tier4: 500,
    apply_ridge_shift: true
  });


  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedHours, setExpandedHours] = useState({});
  const [expandAllFixingII, setExpandAllFixingII] = useState(false);
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [, setShowHourlyPerformance] = useState(false);
  const [hourlyPerformanceData, setHourlyPerformanceData] = useState([]);

  // Automatically calculate hourly performance when results are available
  useEffect(() => {
    if (!results || !results.results) {
      return;
    }

    const hourlyPnL = {};
    for (let i = 0; i < 24; i++) {
      hourlyPnL[i] = 0;
    }

    Object.values(results.results).forEach(dayData => {
      if (dayData.ida1_data_missing || (formData.apply_ridge_shift && dayData.shift_available === false)) return;
      if (dayData.hourly_results && Array.isArray(dayData.hourly_results)) {
        dayData.hourly_results.forEach(hourData => {
          (hourData.quarterly_results || []).forEach(qr => {
            hourlyPnL[hourData.hour] += ((qr.margin_close || {}).pnl || 0) + ((qr.q50_closing || {}).pnl || 0)
              + ((qr.ida1_q50_close || {}).pnl || 0) + ((qr.ida1_bucket_close || {}).pnl || 0);
          });
        });
      }
    });

    const chartData = Object.entries(hourlyPnL).map(([hour, pnl]) => ({
      hour: displayHours1to24 ? parseInt(hour) + 1 : parseInt(hour),
      pnl: parseFloat(pnl.toFixed(2))
    }));

    setHourlyPerformanceData(chartData);
    setShowHourlyPerformance(true);
  }, [displayHours1to24, formData.apply_ridge_shift, results]);

  useEffect(() => {
    if (formData.probabilistic_model !== V5_PROBABILISTIC_MODEL) {
      return;
    }

    setFormData((prev) => {
      const next = {
        ...prev,
        ...V5_DEFAULTS
      };
      const changed = Object.keys(V5_DEFAULTS).some((key) => next[key] !== prev[key]);
      return changed ? next : prev;
    });
  }, [formData.probabilistic_model]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const runCombinedAnalysis = async (overrideFormData = null) => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    setShowHourlyPerformance(false);
    setHourlyPerformanceData([]);
    
    try {
      const dataToUse = overrideFormData || formData;
      
      const formDataToSend = new FormData();
      Object.entries(dataToUse).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetchPost('/ida1-results', formDataToSend);

      const data = await response.json();
      
      if (data.status === 'success') {
        setResults(data);
      } else if (data.detail) {
        // Handle validation errors
        setError('Validation error: ' + JSON.stringify(data.detail));
      } else {
        setError(data.message || 'Analysis failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error running combined analysis:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (date) => {
    setExpandedRows(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const computePnlByCategory = (hourlyResults) => {
    let cat2 = 0, cat3 = 0;
    (hourlyResults || []).forEach(hour => {
      (hour.quarterly_results || []).forEach(qr => {
        cat2 += (qr.margin_close?.pnl || 0) + (qr.q50_closing?.pnl || 0) + (qr.ida1_q50_close?.pnl || 0);
        cat3 += (qr.ida1_bucket_close?.pnl || 0);
      });
    });
    return { fixIArbPnl: cat2, fixIIIDA1ArbPnl: cat3 };
  };

  const renderBaseActionsTable = (data, date) => {
    const baseActionData = data.base_action_results || [];

    if (!data.base_action || data.base_action === 'none' || baseActionData.length === 0) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
          <p className="text-gray-500 text-sm">No base actions taken</p>
        </div>
      );
    }

    const ordersData = baseActionData.filter(h => h.base_fix_i_action || h.base_fix_ii_action || h.base_ida1_action);
    if (ordersData.length === 0) return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
        <p className="text-gray-500 text-sm">No base orders executed</p>
      </div>
    );

    // Build a map of quarterly results by hour for base closing detail
    const hourlyResults = data.hourly_results || [];
    const qrByHour = {};
    hourlyResults.forEach(hr => {
      if (hr.quarterly_results) {
        qrByHour[hr.hour] = hr.quarterly_results;
      }
    });

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
        <div className="mb-2 p-2 bg-gray-100 rounded">
          <span className="text-sm text-gray-700">
            Initial {data.base_action} of {data.base_volume} units at base price {formatNumber(data.base_price)} for all hours
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Model Pred</th>
                <th colSpan="5" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix I</th>
                <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-l">QH</th>
                <th colSpan="3" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix II</th>
                <th colSpan="3" className="px-2 py-2 text-center text-xs font-medium text-purple-600 uppercase border-l">IDA1</th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Vol</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Executed</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border-l">Volume</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Residual</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border-l">Volume</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Residual</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ordersData.map((row, idx) => {
                const bgColor = row.base_pnl > 0 ? 'bg-green-50' : row.base_pnl < 0 ? 'bg-red-50' : '';
                const quarters = qrByHour[row.hour] || [];
                const baseVolPerQtr = data.base_volume;
                const qhLabels = [':15', ':30', ':45', ':00'];
                const hourLabel = displayHours1to24 ? row.hour + 1 : row.hour;

                // Check if hour is fully closed on Fix I (no Fix II or IDA1 activity)
                const hasFixIIOrIDA1 = quarters.some(qr => {
                  const bq = qr.base_q50_close || {};
                  const ib = qr.ida1_base_close || {};
                  const mb = (qr.margin_close || {}).base_portion || 0;
                  return (bq.pnl && bq.pnl !== 0) || (ib.pnl && ib.pnl !== 0) || mb > 0.001
                    || (bq.executed) || (ib.executed);
                }) || (row.base_fix_ii_volume > 0) || (row.base_ida1_volume > 0);

                if (!hasFixIIOrIDA1) {
                  // Single row — fully closed on Fix I
                  return (
                    <tr key={idx} className={`${bgColor} border-t-2 border-gray-300`}>
                      <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{hourLabel}:00</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">{formatNumber(row.fix_ii_prediction)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                        {row.base_fix_i_action && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            row.base_fix_i_action === 'buy' ? 'bg-green-200 text-green-800' :
                            row.base_fix_i_action === 'sell' ? 'bg-red-200 text-red-800' :
                            'bg-gray-200 text-gray-800'
                          }`}>{row.base_fix_i_action}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">{formatNumber(row.base_fix_i_order_price)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">{formatNumber(row.base_fix_i_volume || 0)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">{formatNumber(row.fix_i_price)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                        {row.base_fix_i_volume > 0 ? <span className="text-green-600">✓</span> : <span className="text-red-600">✗</span>}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-400 border-l">-</td>
                      <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-400 border-l">-</td>
                      <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-400">-</td>
                      <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-400">-</td>
                      <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-400 border-l">-</td>
                      <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-400">-</td>
                      <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-400">-</td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                        row.base_pnl > 0 ? 'text-green-600' : row.base_pnl < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>{formatNumber(row.base_pnl)}</td>
                    </tr>
                  );
                }

                const numQtrs = Math.max(quarters.length, 4);

                return (
                  <React.Fragment key={idx}>
                    {Array.from({ length: numQtrs }).map((_, qi) => {
                      const qr = quarters[qi] || {};
                      const baseQ50 = qr.base_q50_close || {};
                      const ida1Base = qr.ida1_base_close || {};
                      const marginBase = (qr.margin_close || {}).base_portion || 0;
                      const fixIIVol = baseQ50.executed ? baseQ50.volume : marginBase;
                      const fixIIPnl = (baseQ50.pnl || 0);
                      const ida1Vol = ida1Base.executed ? ida1Base.volume : 0;
                      const ida1Pnl = ida1Base.pnl || 0;
                      const residualAfterFixII = Math.max(0, baseVolPerQtr - fixIIVol);
                      const residualAfterIDA1 = Math.max(0, residualAfterFixII - ida1Vol);
                      const isFirst = qi === 0;
                      const qhLabel = `${hourLabel}${qhLabels[qi] || ''}`;

                      return (
                        <tr key={`${idx}-${qi}`} className={`${bgColor} ${isFirst ? 'border-t-2 border-gray-300' : ''}`}>
                          {isFirst && (
                            <>
                              <td rowSpan={numQtrs} className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900 align-top">
                                {hourLabel}:00
                              </td>
                              <td rowSpan={numQtrs} className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 align-top">
                                {formatNumber(row.fix_ii_prediction)}
                              </td>
                              <td rowSpan={numQtrs} className="px-2 py-2 whitespace-nowrap text-sm text-center border-l align-top">
                                {row.base_fix_i_action && (
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    row.base_fix_i_action === 'buy' ? 'bg-green-200 text-green-800' :
                                    row.base_fix_i_action === 'sell' ? 'bg-red-200 text-red-800' :
                                    'bg-gray-200 text-gray-800'
                                  }`}>{row.base_fix_i_action}</span>
                                )}
                              </td>
                              <td rowSpan={numQtrs} className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 align-top">
                                {formatNumber(row.base_fix_i_order_price)}
                              </td>
                              <td rowSpan={numQtrs} className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 align-top">
                                {formatNumber(row.base_fix_i_volume || 0)}
                              </td>
                              <td rowSpan={numQtrs} className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 align-top">
                                {formatNumber(row.fix_i_price)}
                              </td>
                              <td rowSpan={numQtrs} className="px-2 py-2 whitespace-nowrap text-sm text-center align-top">
                                {row.base_fix_i_volume > 0 ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-red-600">✗</span>
                                )}
                              </td>
                            </>
                          )}
                          <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-500 border-l">{qhLabel}</td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900 border-l">
                            {fixIIVol > 0.001 ? fixIIVol.toFixed(1) : '-'}
                          </td>
                          <td className={`px-2 py-1 whitespace-nowrap text-sm text-right font-semibold ${
                            fixIIPnl > 0 ? 'text-green-600' : fixIIPnl < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {fixIIPnl !== 0 ? formatNumber(fixIIPnl) : '-'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-500">
                            {residualAfterFixII > 0.001 ? residualAfterFixII.toFixed(1) : '-'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900 border-l">
                            {ida1Vol > 0.001 ? ida1Vol.toFixed(1) : '-'}
                          </td>
                          <td className={`px-2 py-1 whitespace-nowrap text-sm text-right font-semibold ${
                            ida1Pnl > 0 ? 'text-green-600' : ida1Pnl < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {ida1Pnl !== 0 ? formatNumber(ida1Pnl) : '-'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-500">
                            {residualAfterIDA1 > 0.001 ? residualAfterIDA1.toFixed(1) : '-'}
                          </td>
                          {isFirst && (
                            <td rowSpan={numQtrs} className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold align-top ${
                              row.base_pnl > 0 ? 'text-green-600' :
                              row.base_pnl < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {formatNumber(row.base_pnl)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderHourlyTradingTable = (hourlyData, date) => {
    if (!hourlyData || hourlyData.length === 0) return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Fixing I</h3>
        <p className="text-gray-500 text-sm">No hourly trading data</p>
      </div>
    );

    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Fixing I</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border-l">Q05</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q10</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q20</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q30</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q40</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q50</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q60</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q70</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q80</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q90</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q95</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Fix I Action</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Trend</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hourlyData.map((hour, idx) => {
                const fix_i_exec = hour.fix_i_execution || {};
                const quantiles = hour.quantiles || {};
                const pnl = (hour.quarterly_results || []).reduce((sum, qr) => {
                  return sum + ((qr.margin_close || {}).pnl || 0) + ((qr.q50_closing || {}).pnl || 0)
                    + ((qr.ida1_q50_close || {}).pnl || 0);
                }, 0);

                // Determine which quantiles triggered
                const triggeredBuyQuantiles = [];
                const triggeredSellQuantiles = [];

                if (fix_i_exec.orders_executed) {
                  fix_i_exec.orders_executed.forEach((order) => {
                    // Use actual_quantile for display (the shifted value), fallback to quantile for backwards compatibility
                    const displayQuantile = order.actual_quantile || order.quantile;
                    if (fix_i_exec.action === 'buy') {
                      triggeredBuyQuantiles.push(displayQuantile);
                    } else if (fix_i_exec.action === 'sell') {
                      triggeredSellQuantiles.push(displayQuantile);
                    }
                  });
                }

                // Determine which quantiles are active for orders based on shift direction
                // shiftDirection: 1 = down (Fix I pred > Fix II pred), -1 = up (Fix II pred > Fix I pred), 0 = no shift
                const getActiveOrderQuantiles = (shift) => {
                  if (shift > 0) {
                    // Shift down: buy q05,q10,q20; sell q60,q70,q80
                    return ['q05', 'q10', 'q20', 'q60', 'q70', 'q80'];
                  } else if (shift < 0) {
                    // Shift up: buy q20,q30,q40; sell q80,q90,q95
                    return ['q20', 'q30', 'q40', 'q80', 'q90', 'q95'];
                  } else {
                    // No shift: buy q10,q20,q30; sell q70,q80,q90
                    return ['q10', 'q20', 'q30', 'q70', 'q80', 'q90'];
                  }
                };

                // Check if quantiles were shifted based on prediction comparison
                const shiftDirection = hour.shift_direction;
                const activeQuantiles = getActiveOrderQuantiles(shiftDirection);

                const getCellClass = (quantile, price, fixPrice) => {
                  let classes = [];

                  // Add diagonal stripes for active order quantiles
                  if (activeQuantiles.includes(quantile)) {
                    classes.push('bg-stripes');
                  }

                  if (triggeredBuyQuantiles.includes(quantile)) {
                    classes.push('bg-green-100 font-semibold');
                  } else if (triggeredSellQuantiles.includes(quantile)) {
                    classes.push('bg-red-100 font-semibold');
                  } else {
                    // Highlight potential triggers that didn't execute (buy quantiles)
                    if (['q05', 'q10', 'q20', 'q30', 'q40'].includes(quantile) && fixPrice < price) {
                      classes.push('bg-green-50');
                    }
                    // Highlight potential triggers that didn't execute (sell quantiles)
                    if (['q60', 'q70', 'q80', 'q90', 'q95'].includes(quantile) && fixPrice > price) {
                      classes.push('bg-red-50');
                    }
                  }

                  return classes.join(' ');
                };
                const noAction = !fix_i_exec.action || fix_i_exec.action === 'none';

                return (
                  <tr key={idx} className={
                    pnl > 0 ? 'bg-green-50' :
                    pnl < 0 ? 'bg-red-50' :
                    noAction ? 'bg-gray-50' : ''
                  }>
                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {displayHours1to24 ? hour.hour + 1 : hour.hour}:00
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(hour.fix_i_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(hour.fix_ii_price)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 border-l ${getCellClass('q05', quantiles.q05, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q05)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q10', quantiles.q10, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q10)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q20', quantiles.q20, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q20)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q30', quantiles.q30, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q30)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q40', quantiles.q40, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q40)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                      {formatNumber(quantiles.q50)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q60', quantiles.q60, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q60)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q70', quantiles.q70, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q70)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q80', quantiles.q80, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q80)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q90', quantiles.q90, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q90)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q95', quantiles.q95, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q95)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {fix_i_exec.action ? (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          fix_i_exec.action === 'buy' ? 'bg-green-200 text-green-800' :
                          fix_i_exec.action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {fix_i_exec.action}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-center">
                      {shiftDirection < 0 ? (
                        <span className="text-green-600 font-medium">up</span>
                      ) : shiftDirection > 0 ? (
                        <span className="text-red-600 font-medium">down</span>
                      ) : (
                        <span className="text-gray-400"></span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {fix_i_exec.total_volume || '-'}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                      pnl > 0 ? 'text-green-600' : pnl < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {formatNumber(pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderQuarterlyDetailTable = (hourlyData, date, data) => {
    // Build hour groups from hourly data
    const hourGroups = [];
    (hourlyData || []).forEach(hour => {
      if (hour.quarterly_results && Array.isArray(hour.quarterly_results)) {
        const quarters = hour.quarterly_results.map(qr => ({
          ...qr,
          fix_i_price: hour.fix_i_price,
          fix_i_action: hour.fix_i_execution?.action
        }));
        hourGroups.push({ hour, quarters });
      }
    });

    // Build base residual lookup by hour
    const baseResidualByHour = {};
    const baseAction = data?.base_action;
    (data?.base_action_results || []).forEach(row => {
      baseResidualByHour[row.hour] = row.base_residual || 0;
    });

    if (hourGroups.length === 0) return (
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-purple-800 mb-2">Fixing II</h3>
        <p className="text-gray-500 text-sm">No quarterly data available</p>
      </div>
    );

    const toggleHourExpand = (hourKey) => {
      setExpandedHours(prev => ({ ...prev, [hourKey]: !prev[hourKey] }));
    };

    const isHourExpanded = (hourKey) => expandAllFixingII || !!expandedHours[hourKey];

    // Helper to format quarter label as time string, e.g. qh=0 hour=0 -> "00:15"
    const formatQuarterTime = (hour, qh) => {
      const qhMinutes = [15, 30, 45, 0];
      const displayHour = qh === 3 ? hour + 1 : hour;
      const hourStr = String(displayHour).padStart(2, '0');
      return `${hourStr}:${String(qhMinutes[qh]).padStart(2, '0')}`;
    };

    return (
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-purple-800">Fixing II</h3>
          <label className="flex items-center space-x-2 cursor-pointer">
            <span className="text-sm text-gray-600">Expand all</span>
            <div
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${expandAllFixingII ? 'bg-purple-600' : 'bg-gray-300'}`}
              onClick={() => setExpandAllFixingII(!expandAllFixingII)}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${expandAllFixingII ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
            </div>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">QH</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Position</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Margin Vol</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Margin Close</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Q40-60 Close</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">IDA1 Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border-l">q05</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">q10</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">q20</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase font-bold">q50</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">q80</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">q90</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">q95</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">IDA1 Exec</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">IDA1 PnL</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hourGroups.map((hg, groupIdx) => {
                const { hour: hourData, quarters: group } = hg;
                const hourArbPnl = group.reduce((sum, qr) => {
                  return sum + ((qr.margin_close || {}).pnl || 0) + ((qr.q50_closing || {}).pnl || 0)
                    + ((qr.ida1_q50_close || {}).pnl || 0) + ((qr.ida1_bucket_close || {}).pnl || 0);
                }, 0);
                const hourIda1BucketPnl = group.reduce((sum, qr) => sum + ((qr.ida1_bucket_close || {}).pnl || 0), 0);
                const hourNum = displayHours1to24 ? group[0].hour + 1 : group[0].hour;
                const hourLabel = `${hourNum}:00`;
                const hourKey = `${date}_${group[0].hour}`;
                const expanded = isHourExpanded(hourKey);

                // Compute full open position going into Fix II (Fix I + BASE residual)
                const fixIAction = hourData.fix_i_execution?.action;
                const fixIVol = hourData.fix_i_execution?.total_volume || 0;
                const baseResidual = baseResidualByHour[group[0].hour] || 0;
                let longPosition = 0;
                let shortPosition = 0;
                if (fixIAction === 'buy') longPosition += fixIVol;
                else if (fixIAction === 'sell') shortPosition += fixIVol;
                if (baseResidual > 0 && baseAction === 'buy') longPosition += baseResidual;
                else if (baseResidual > 0 && baseAction === 'sell') shortPosition += baseResidual;
                const netPosition = longPosition - shortPosition;
                let positionSummary = '-';
                if (netPosition > 0) {
                  positionSummary = `Long ${netPosition.toFixed(1)} MW`;
                } else if (netPosition < 0) {
                  positionSummary = `Short ${Math.abs(netPosition).toFixed(1)} MW`;
                }

                // Average quantiles across quarters
                const qKeys = ['q05', 'q10', 'q20', 'q50', 'q80', 'q90', 'q95'];
                const avgQuantiles = {};
                qKeys.forEach(k => {
                  let sum = 0, count = 0;
                  group.forEach(qr => {
                    const v = (qr.ida1_quantiles || {})[k];
                    if (v != null) { sum += v; count++; }
                  });
                  avgQuantiles[k] = count > 0 ? sum / count : null;
                });

                const noAction = !hourData.fix_i_execution?.action || hourData.fix_i_execution?.action === 'none';

                return (
                  <React.Fragment key={groupIdx}>
                    {/* Hourly summary row (always visible) */}
                    <tr
                      className={`cursor-pointer hover:bg-purple-100 border-t-2 border-gray-300 ${
                        hourArbPnl > 0 ? 'bg-green-50' : hourArbPnl < 0 ? 'bg-red-50' : noAction ? 'bg-gray-50' : 'bg-purple-50'
                      }`}
                      onClick={() => toggleHourExpand(hourKey)}
                    >
                      <td className="px-2 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">
                        <span className="inline-flex items-center space-x-1">
                          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span>{hourLabel}</span>
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-center text-gray-700">
                        {positionSummary}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-400"></td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-center text-gray-400"></td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-center text-gray-400">
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                        {formatNumber(hourData.fix_ii_price)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                        {formatNumber(hourData.ida1_price)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500 border-l">{formatNumber(avgQuantiles.q05)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500">{formatNumber(avgQuantiles.q10)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500">{formatNumber(avgQuantiles.q20)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-medium">{formatNumber(avgQuantiles.q50)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500">{formatNumber(avgQuantiles.q80)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500">{formatNumber(avgQuantiles.q90)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500">{formatNumber(avgQuantiles.q95)}</td>
                      <td colSpan={2} className="px-2 py-2 whitespace-nowrap text-sm text-center text-gray-400">
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-center text-gray-400">
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-bold ${
                        hourIda1BucketPnl > 0 ? 'text-green-600' : hourIda1BucketPnl < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {hourIda1BucketPnl !== 0 ? formatNumber(hourIda1BucketPnl) : '-'}
                      </td>
                    </tr>
                    {/* QH detail rows (visible when expanded) */}
                    {expanded && group.map((qr, qIdx) => {
                      const qrArbPnl = ((qr.margin_close || {}).pnl || 0) + ((qr.q50_closing || {}).pnl || 0)
                        + ((qr.ida1_q50_close || {}).pnl || 0) + ((qr.ida1_bucket_close || {}).pnl || 0);
                      const bgColor = qrArbPnl > 0 ? 'bg-green-50' : qrArbPnl < 0 ? 'bg-red-50' : '';
                      const quantiles = qr.ida1_quantiles || {};
                      const fixIIPrice = qr.fix_ii_qtr_price;
                      const bucketAction = qr.fix_ii_bucket?.action;
                      const bucketOrders = qr.fix_ii_bucket?.orders_executed || [];
                      const blockedOrders = qr.fix_ii_bucket?.orders_blocked || [];
                      const triggeredBuyQs = bucketAction === 'buy' ? bucketOrders.map(o => o.quantile) : [];
                      const triggeredSellQs = bucketAction === 'sell' ? bucketOrders.map(o => o.quantile) : [];
                      const blockedQs = blockedOrders.map(o => o.quantile);

                      const getQCellClass = (q) => {
                        let classes = 'px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900';
                        const buyActiveQs = ['q05', 'q10', 'q20'];
                        const sellActiveQs = ['q80', 'q90', 'q95'];
                        if (buyActiveQs.includes(q) || sellActiveQs.includes(q)) {
                          classes += ' bg-stripes';
                        }
                        if (triggeredBuyQs.includes(q)) {
                          classes += ' bg-green-100 font-semibold';
                        } else if (triggeredSellQs.includes(q)) {
                          classes += ' bg-red-100 font-semibold';
                        } else if (blockedQs.includes(q)) {
                          classes += ' bg-gray-200';
                        }
                        return classes;
                      };

                      // Position: full open position going into Fix II (Fix I + BASE residual)
                      const marginClose = qr.margin_close || {};
                      const q50Close = qr.q50_closing || {};
                      let positionText = '-';
                      let positionClass = 'text-gray-400';
                      if (netPosition > 0) {
                        positionText = `Long ${formatNumber(netPosition)} MW`;
                        positionClass = 'text-green-700';
                      } else if (netPosition < 0) {
                        positionText = `Short ${formatNumber(Math.abs(netPosition))} MW`;
                        positionClass = 'text-red-700';
                      }

                      const baseQ50Close = qr.base_q50_close || {};
                      const marginBasePortion = marginClose.base_portion || 0;
                      const curveNetVol = qr.curve_net_volume;
                      let q50Text = '-';
                      let q50BadgeClass = 'bg-gray-200 text-gray-600';
                      if (curveNetVol !== undefined && curveNetVol !== null) {
                        // Q4060 Close: show the q4060 interpolated volume from Pass 2
                        const q4060Vol = q50Close.volume || 0;
                        if (q4060Vol > 0.001) {
                          q50Text = `${q4060Vol.toFixed(1)} MW \u2713`;
                          q50BadgeClass = q50Close.action === 'sell'
                            ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800';
                        }
                      } else if (q50Close.action) {
                        if (q50Close.executed) {
                          q50Text = `${q50Close.action} \u2713`;
                          q50BadgeClass = q50Close.action === 'sell' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800';
                        } else {
                          q50Text = `${q50Close.action} \u2717`;
                        }
                      } else if (baseQ50Close.action) {
                        const combinedVol = (baseQ50Close.executed ? baseQ50Close.volume : 0) + marginBasePortion;
                        if (combinedVol > 0) {
                          q50Text = `${combinedVol.toFixed(1)} MW \u2713`;
                          q50BadgeClass = baseQ50Close.action === 'sell' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800';
                        } else {
                          q50Text = `${baseQ50Close.action} \u2717`;
                        }
                      } else if (marginBasePortion > 0.001) {
                        q50Text = `${marginBasePortion.toFixed(1)} MW \u2713`;
                        q50BadgeClass = baseAction === 'sell' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800';
                      }

                      // IDA1 Exec indicator
                      const ida1Q50 = qr.ida1_q50_close || {};
                      const ida1Bucket = qr.ida1_bucket_close || {};
                      const ida1Base = qr.ida1_base_close || {};
                      const ida1Executed = ida1Q50.executed || ida1Bucket.executed || ida1Base.executed;
                      const ida1Attempted = !!(ida1Q50.action || ida1Bucket.action || ida1Base.action);

                      return (
                        <tr key={`${groupIdx}-${qIdx}`} className={bgColor}>
                          <td className="px-2 py-1 whitespace-nowrap text-sm font-medium text-gray-900 pl-6">
                            {formatQuarterTime(group[0].hour, qr.qh)}
                          </td>
                          <td className={`px-2 py-1 whitespace-nowrap text-sm text-center font-medium ${positionClass}`}>
                            {positionText}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900">
                            {marginClose.intended_volume > 0 ? (
                              <span>
                                {(marginClose.volume || 0).toFixed(1)} MW
                                {marginClose.executed ? <span className="text-green-600 ml-1">{'\u2713'}</span> : <span className="text-red-600 ml-1">{'\u2717'}</span>}
                              </span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-center">
                            {marginClose.action ? (
                              <span className={`px-1 py-0.5 rounded text-xs font-medium ${
                                marginClose.executed
                                  ? 'bg-orange-200 text-orange-800'
                                  : 'bg-gray-200 text-gray-600'
                              }`}>{marginClose.action} @{formatNumber(marginClose.order_price)}</span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-center">
                            {q50Text !== '-' ? (
                              <span className={`px-1 py-0.5 rounded text-xs font-medium ${q50BadgeClass}`}>{q50Text}</span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900">
                            {formatNumber(fixIIPrice)}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900">
                            {formatNumber(qr.ida1_qtr_price)}
                          </td>
                          <td className={getQCellClass('q05') + ' border-l'}>{formatNumber(quantiles.q05)}</td>
                          <td className={getQCellClass('q10')}>{formatNumber(quantiles.q10)}</td>
                          <td className={getQCellClass('q20')}>{formatNumber(quantiles.q20)}</td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                            {formatNumber(quantiles.q50)}
                          </td>
                          <td className={getQCellClass('q80')}>{formatNumber(quantiles.q80)}</td>
                          <td className={getQCellClass('q90')}>{formatNumber(quantiles.q90)}</td>
                          <td className={getQCellClass('q95')}>{formatNumber(quantiles.q95)}</td>
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-center">
                            {(() => {
                              const displayAction = bucketAction || (marginBasePortion > 0.001 ? marginClose.action : null) || baseQ50Close.action;
                              return displayAction ? (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  displayAction === 'buy' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                }`}>{displayAction}</span>
                              ) : <span className="text-gray-400">-</span>;
                            })()}
                          </td>
                          {(() => {
                            const ceilVol = (v) => Math.ceil(v * 10) / 10;
                            const bucketVolMW = ceilVol(qr.fix_ii_bucket?.total_volume || 0);
                            const marginClosedFixIMW = ceilVol(marginClose.fix_i_portion || 0);
                            const marginClosedBaseMW = ceilVol(marginClose.base_portion || 0);
                            const q50ClosedMW = ceilVol(q50Close.executed ? q50Close.volume : 0);
                            const unclosedFixIVolMW = Math.max(0, ceilVol(fixIVol - marginClosedFixIMW - q50ClosedMW));
                            const baseQ50CloseData = qr.base_q50_close || {};
                            const baseQ50ClosedMW = ceilVol(baseQ50CloseData.executed ? baseQ50CloseData.volume : 0);
                            const unclosedBaseVolMW = Math.max(0, ceilVol(baseResidual - marginClosedBaseMW - baseQ50ClosedMW));
                            const totalVolMW = ceilVol(bucketVolMW + unclosedFixIVolMW + unclosedBaseVolMW);
                            return (
                              <td className="px-2 py-1 whitespace-nowrap text-sm text-right text-gray-900">
                                {totalVolMW > 0 ? totalVolMW.toFixed(1) : '-'}
                              </td>
                            );
                          })()}
                          <td className="px-2 py-1 whitespace-nowrap text-sm text-center">
                            {ida1Executed ? (
                              <span className="text-green-600 font-medium">{'\u2713'}</span>
                            ) : ida1Attempted ? (
                              <span className="text-red-600 font-medium">{'\u2717'}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          {(() => {
                            const ida1Pnl = ida1Bucket.pnl || 0;
                            return (
                              <td className={`px-2 py-1 whitespace-nowrap text-sm text-right font-semibold ${
                                ida1Pnl > 0 ? 'text-green-600' : ida1Pnl < 0 ? 'text-red-600' : 'text-gray-600'
                              }`}>
                                {ida1Pnl !== 0 ? formatNumber(ida1Pnl) : '-'}
                              </td>
                            );
                          })()}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDateRow = (date, data) => {
    const isExpanded = expandedRows[date];
    const totalPnl = data.daily_pnl || 0;
    const basePnl = data.base_pnl || 0;
    const totalVolume = data.total_volume_traded || 0;
    const isPnLPositive = totalPnl > 0;
    const isPnLNegative = totalPnl < 0;
    const isIDA1Missing = data.ida1_data_missing;
    const isShiftMissing = formData.apply_ridge_shift && data.shift_available === false;
    const isDayDimmed = isIDA1Missing || isShiftMissing;

    // Count hours with Fix II volume = 0 (for hourly trading)
    let zeroVolumeHoursCount = 0;

    if (data.hourly_results) {
      data.hourly_results.forEach(hour => {
        // Check if there's Fix II action for this hour with zero volume
        if (hour.fix_ii_action && hour.fix_ii_volume === 0) {
          zeroVolumeHoursCount++;
        }
      });
    }

    return (
      <div key={date} className={`border rounded-lg mb-4 overflow-hidden ${isDayDimmed ? 'opacity-50' : ''}`}>
        <div
          className={`p-4 cursor-pointer transition-colors overflow-x-auto ${
            isDayDimmed ? 'bg-gray-100 hover:bg-gray-200' :
            isPnLPositive ? 'bg-green-50 hover:bg-green-100' :
            isPnLNegative ? 'bg-red-50 hover:bg-red-100' :
            'bg-gray-50 hover:bg-gray-100'
          }`}
          onClick={() => toggleRow(date)}
        >
          <div className="flex items-center justify-between min-w-max">
            <div className="flex items-center space-x-4">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="font-semibold">{date}</span>
              {isIDA1Missing && (
                <span className="px-2 py-1 bg-gray-300 text-gray-600 rounded-full text-xs font-medium">
                  No IDA1 Data
                </span>
              )}
              {isShiftMissing && !isIDA1Missing && (
                <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs font-medium">
                  No Ridge Shift
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                data.base_action === 'buy' ? 'bg-green-200 text-green-800' :
                data.base_action === 'sell' ? 'bg-red-200 text-red-800' :
                'bg-gray-200 text-gray-800'
              }`}>
                {data.base_action === 'none' ? 'idle' : data.base_action}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {Math.round(totalVolume)} MWh
              </span>
              {/* Display count of hours with Fix II volume = 0 */}
              {zeroVolumeHoursCount > 0 && (
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                  {zeroVolumeHoursCount} H
                </span>
              )}
            </div>
            <div className="flex flex-col space-y-1 text-sm">
              {/* First row: Base | Model Pred, Fix I, Fix II | IDA1 Pred, IDA1 | Preds Diff */}
              <div className="flex items-center space-x-3">
                <div>
                  <span className="text-gray-500">Base:</span>
                  <span className="ml-1 font-semibold">{formatNumber(data.base_price)}</span>
                </div>
                <div className="border-l border-gray-300 pl-3 flex items-center space-x-3">
                  <div>
                    <span className="text-gray-500">Model Pred:</span>
                    <span className="ml-1 font-semibold">{formatNumber(data.avg_q50_prediction)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Shift:</span>
                    <span className={`ml-1 font-semibold ${data.ridge_shift > 0 ? 'text-green-600' : data.ridge_shift < 0 ? 'text-red-600' : ''}`}>
                      {data.ridge_shift !== null && data.ridge_shift !== undefined ? `${data.ridge_shift > 0 ? '+' : ''}${data.ridge_shift}` : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fix I:</span>
                    <span className="ml-1 font-semibold">{formatNumber(data.avg_fix_i_price)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fix II:</span>
                    <span className="ml-1 font-semibold">{formatNumber(data.avg_fix_ii_price)}</span>
                  </div>
                </div>
                <div className="border-l border-gray-300 pl-3 flex items-center space-x-3">
                  <div>
                    <span className="text-gray-500">IDA1 Pred:</span>
                    <span className="ml-1 font-semibold">{formatNumber(data.avg_ida1_q50_prediction)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">IDA1:</span>
                    <span className="ml-1 font-semibold">{formatNumber(data.avg_ida1_price)}</span>
                  </div>
                </div>
                <div className="border-l border-gray-300 pl-3">
                  <span className="text-gray-500">Preds Diff:</span>
                  <span className={`ml-1 font-semibold ${
                    data.base_action_skipped_by_preds_diff || data.fixing_trades_skipped_by_preds_diff
                      ? (data.preds_diff > 0 ? 'text-red-600' : 'text-green-600')
                      : 'text-gray-900'
                  }`}>
                    {data.preds_diff !== null && data.preds_diff !== undefined
                      ? `${data.preds_diff > 0 ? '+' : ''}${data.preds_diff.toFixed(2)}%`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              {/* Second row: Base PnL, Fix I Arb PnL, Fix II→IDA1 PnL, Daily PnL */}
              {(() => {
                const { fixIArbPnl, fixIIIDA1ArbPnl } = computePnlByCategory(data.hourly_results);
                return (
                  <div className="flex items-center space-x-4">
                    <div>
                      <span className="text-gray-500">Base PnL:</span>
                      <span className={`ml-2 font-semibold ${
                        basePnl > 0 ? 'text-green-600' :
                        basePnl < 0 ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {formatNumber(basePnl)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Fix I Arb:</span>
                      <span className={`ml-2 font-semibold ${
                        fixIArbPnl > 0 ? 'text-green-600' :
                        fixIArbPnl < 0 ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {formatNumber(fixIArbPnl)}
                      </span>
                    </div>
                    <div>
                      <span className="text-purple-600">Fix II→IDA1:</span>
                      <span className={`ml-2 font-semibold ${
                        fixIIIDA1ArbPnl > 0 ? 'text-green-600' :
                        fixIIIDA1ArbPnl < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {formatNumber(fixIIIDA1ArbPnl)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Daily PnL:</span>
                      <span className={`ml-2 font-semibold ${
                        totalPnl > 0 ? 'text-green-600' :
                        totalPnl < 0 ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {formatNumber(totalPnl)}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 bg-white border-t">
            {renderBaseActionsTable(data, date)}
            {renderHourlyTradingTable(data.hourly_results, date)}
            {renderQuarterlyDetailTable(data.hourly_results, date, data)}

            {/* Trading Summary for this date */}
            {(() => {
              const { fixIArbPnl, fixIIIDA1ArbPnl } = computePnlByCategory(data.hourly_results);
              const catBasePnl = data.base_pnl || 0;
              const totalCatPnl = catBasePnl + fixIArbPnl + fixIIIDA1ArbPnl;

              return (
                <div className="mt-6 border rounded-lg p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Summary</h3>

                  {/* PnL by Category */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className={`border rounded p-3 ${catBasePnl > 0 ? 'bg-green-50' : catBasePnl < 0 ? 'bg-red-50' : 'bg-white'}`}>
                      <h5 className="font-medium text-gray-600 mb-1 text-sm">Base PnL</h5>
                      <span className={`text-lg font-bold ${
                        catBasePnl > 0 ? 'text-green-700' : catBasePnl < 0 ? 'text-red-700' : 'text-gray-700'
                      }`}>{formatNumber(catBasePnl)}</span>
                    </div>
                    <div className={`border rounded p-3 ${fixIArbPnl > 0 ? 'bg-green-50' : fixIArbPnl < 0 ? 'bg-red-50' : 'bg-white'}`}>
                      <h5 className="font-medium text-gray-600 mb-1 text-sm">Fix I Arb PnL</h5>
                      <span className={`text-lg font-bold ${
                        fixIArbPnl > 0 ? 'text-green-700' : fixIArbPnl < 0 ? 'text-red-700' : 'text-gray-700'
                      }`}>{formatNumber(fixIArbPnl)}</span>
                    </div>
                    <div className={`border rounded p-3 ${fixIIIDA1ArbPnl > 0 ? 'bg-green-50' : fixIIIDA1ArbPnl < 0 ? 'bg-red-50' : 'bg-white'}`}>
                      <h5 className="font-medium text-purple-600 mb-1 text-sm">Fix II→IDA1 PnL</h5>
                      <span className={`text-lg font-bold ${
                        fixIIIDA1ArbPnl > 0 ? 'text-green-700' : fixIIIDA1ArbPnl < 0 ? 'text-red-700' : 'text-gray-700'
                      }`}>{formatNumber(fixIIIDA1ArbPnl)}</span>
                    </div>
                    <div className={`border rounded p-3 ${totalCatPnl > 0 ? 'bg-green-100' : totalCatPnl < 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                      <h5 className="font-medium text-gray-600 mb-1 text-sm">Total PnL</h5>
                      <span className={`text-lg font-bold ${
                        totalCatPnl > 0 ? 'text-green-800' : totalCatPnl < 0 ? 'text-red-800' : 'text-gray-800'
                      }`}>{formatNumber(totalCatPnl)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Bison IDA1</h1>
          <p className="text-gray-600">Run combined step analysis with Probabilistic Model</p>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Activity size={16} />
                <span>Fix I Model Spread</span>
              </label>
              <input
                type="number"
                value={formData.fix_i_model_spread}
                onChange={(e) => handleInputChange('fix_i_model_spread', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="0.1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} />
                <span>Base Volume</span>
              </label>
              <input
                type="number"
                value={formData.base_volume}
                onChange={(e) => handleInputChange('base_volume', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fix I Model</span>
              </label>
              <select
                value={formData.fix_i_model}
                onChange={(e) => handleInputChange('fix_i_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0">v1.0.0</option>
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.1">v1.0.1</option>
                <option value="v1.0.2">v1.0.2</option>
                <option value="v1.0.3">v1.0.3</option>
                <option value="v1.0.3.1">v1.0.3.1</option>
                <option value="v2.0.0">v2.0.0</option>
                <option value="v2.0.0-cp14">v2.0.0-cp14</option>
                <option value="v2.0.0-exaa">v2.0.0-exaa</option>
                <option value="v2.0.0-hourly">v2.0.0-hourly</option>
                <option value="v2.0.0-l1">v2.0.0-l1</option>
                <option value="v2.0.0-narx">v2.0.0-narx</option>
                <option value="v2.0.0-narx-single">v2.0.0-narx-single</option>
                <option value="v4.0.0">v4.0.0</option>
                <option value="v4.1.0">v4.1.0</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fix II Model</span>
              </label>
              <select
                value={formData.fix_ii_model}
                onChange={(e) => handleInputChange('fix_ii_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.0-fix2-cp14">v1.0.0-fix2-cp14</option>
                <option value="v1.0.0-fix2-exaa">v1.0.0-fix2-exaa</option>
                <option value="v2.0.0-fix2">v2.0.0-fix2</option>
                <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                <option value="v2.0.0-narx-fix2">v2.0.0-narx-fix2</option>
                <option value="v4.0.0-fix2">v4.0.0-fix2</option>

              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Probabilistic Model</span>
              </label>
              <select
                value={formData.probabilistic_model}
                onChange={(e) => handleInputChange('probabilistic_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v3.0.0">v3.0.0</option>
                <option value="v3.1.0">v3.1.0</option>
                <option value="v3.1.1">v3.1.1</option>
                <option value="v3.2.0">v3.2.0</option>
                <option value="v3.3.0">v3.3.0</option>
                <option value="v4.0.0">v4.0.0</option>
                <option value="v4.1.0">v4.1.0</option>
                <option value="v5.0.0">v5.0.0</option>
                <option value="fix2-v4.0.0">fix2-v4.0.0</option>
                <option value="fix2-24-v4.0.0">fix2-24-v4.0.0</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>IDA1 Model</span>
              </label>
              <select
                value={formData.ida1_model}
                onChange={(e) => handleInputChange('ida1_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ida1-v1.0.0">ida1-v1.0.0</option>
                <option value="ida1-v1.0.1">ida1-v1.0.1</option>
                <option value="ida1-v1.0.2">ida1-v1.0.2</option>
                <option value="ida1-v1.0.3">ida1-v1.0.3</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <DollarSign size={16} />
                <span>IDA1 Margin</span>
              </label>
              <input
                type="number"
                value={formData.ida1_margin}
                onChange={(e) => handleInputChange('ida1_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Preds Diff Cutoff (%)</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.preds_diff_cutoff}
                onChange={(e) => handleInputChange('preds_diff_cutoff', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Target size={16} />
                <span>Trend Filter Margin (PLN)</span>
              </label>
              <input
                type="number"
                step="1"
                value={formData.prediction_filter_margin}
                onChange={(e) => handleInputChange('prediction_filter_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Bucket Configuration */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Bucket Configuration</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Fix I vs Fix II Bucket 1 Volume</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.bucket1_volume}
                  onChange={(e) => handleInputChange('bucket1_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Fix I vs Fix II Bucket 2 Volume</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.bucket2_volume}
                  onChange={(e) => handleInputChange('bucket2_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Fix I vs Fix II Bucket 3 Volume</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.bucket3_volume}
                  onChange={(e) => handleInputChange('bucket3_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Fix II vs IDA1 Bucket 1 Volume</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.fix_ii_ida1_bucket1_volume}
                  onChange={(e) => handleInputChange('fix_ii_ida1_bucket1_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Fix II vs IDA1 Bucket 2 Volume</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.fix_ii_ida1_bucket2_volume}
                  onChange={(e) => handleInputChange('fix_ii_ida1_bucket2_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Fix II vs IDA1 Bucket 3 Volume</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.fix_ii_ida1_bucket3_volume}
                  onChange={(e) => handleInputChange('fix_ii_ida1_bucket3_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {/* Max IDA1 Position */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Target size={16} />
                  <span>Max IDA1 Avg Volume (%)</span>
                </label>
                <input
                  type="number"
                  step="5"
                  value={formData.max_avg_volume_pct}
                  onChange={(e) => handleInputChange('max_avg_volume_pct', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Target size={16} />
                  <span>Max IDA1 Position (MW)</span>
                </label>
                <input
                  type="number"
                  step="10"
                  value={formData.max_position}
                  onChange={(e) => handleInputChange('max_position', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {/* Fix II Margin Configuration */}
            <div className="grid grid-cols-4 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Margin: Price &lt; 500
                </label>
                <input
                  type="number"
                  step="10"
                  value={formData.margin_tier1}
                  onChange={(e) => handleInputChange('margin_tier1', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Margin: Price 500-700
                </label>
                <input
                  type="number"
                  step="10"
                  value={formData.margin_tier2}
                  onChange={(e) => handleInputChange('margin_tier2', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Margin: Price 700-1000
                </label>
                <input
                  type="number"
                  step="10"
                  value={formData.margin_tier3}
                  onChange={(e) => handleInputChange('margin_tier3', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Margin: Price &gt; 1000
                </label>
                <input
                  type="number"
                  step="10"
                  value={formData.margin_tier4}
                  onChange={(e) => handleInputChange('margin_tier4', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {/* NP Constraint Thresholds */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Bucket 1 NP Constraint (MWh)
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.np_constraint_bucket1}
                  onChange={(e) => handleInputChange('np_constraint_bucket1', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Bucket 2 NP Constraint (MWh)
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.np_constraint_bucket2}
                  onChange={(e) => handleInputChange('np_constraint_bucket2', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Bucket 3 NP Constraint (MWh)
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.np_constraint_bucket3}
                  onChange={(e) => handleInputChange('np_constraint_bucket3', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.apply_ridge_shift}
                onChange={(e) => handleInputChange('apply_ridge_shift', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Apply Ridge Shift</span>
            </label>
          </div>

          <div className="mt-4 flex gap-4">
            <button
              onClick={() => runCombinedAnalysis()}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02]'
              }`}
            >
              <Play size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis'}</span>
            </button>
            
            <button
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowStr = formatDate(tomorrow);
                const updatedFormData = {
                  ...formData,
                  start_date: tomorrowStr,
                  end_date: tomorrowStr
                };
                setFormData(updatedFormData);
                // Run analysis with the updated data
                runCombinedAnalysis(updatedFormData);
              }}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 transform hover:scale-[1.02]'
              }`}
            >
              <Calendar size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis For Tomorrow'}</span>
            </button>
          </div>

          {/* Hour display format toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Results Summary */}
        {results && results.summary && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-3">Summary</h2>

            {/* Overview table */}
            <div className="overflow-x-auto mb-4">
              <table className="text-base">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-2 pr-6 text-left font-medium"></th>
                    <th className="py-2 pr-4 text-left font-medium">Total</th>
                    <th className="py-2 pr-4 text-left font-medium">Profitable</th>
                    <th className="py-2 text-left font-medium">Loss</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  <tr className="border-b border-gray-100">
                    <td className="py-2 pr-6 text-gray-500 font-medium">Days</td>
                    <td className="py-2 pr-4 font-bold">{results.summary.total_days || 0}</td>
                    <td className="py-2 pr-4 text-green-700 font-medium">{results.summary.profitable_days || 0} profitable <span className="text-gray-400 font-normal">({formatPercentage(results.summary.profitable_days_percentage)})</span></td>
                    <td className="py-2 text-red-700 font-medium">{results.summary.loss_days || 0} loss <span className="text-gray-400 font-normal">({formatPercentage(results.summary.loss_days_percentage)})</span></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 pr-6 text-gray-500 font-medium">BASE Actions</td>
                    <td className="py-2 pr-4"></td>
                    <td className="py-2 pr-4 text-green-700 font-medium">{results.summary.base_model_actions?.buy || 0} buy</td>
                    <td className="py-2 text-red-700 font-medium">{results.summary.base_model_actions?.sell || 0} sell <span className="text-gray-400 font-normal">/ {results.summary.base_model_actions?.none || 0} idle</span></td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-6 text-gray-500 font-medium">Avg Daily PnL</td>
                    <td className={`py-2 pr-4 font-bold ${(results.summary.avg_daily_combined_pnl || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`} colSpan="3">
                      {formatNumber(results.summary.avg_daily_combined_pnl)} PLN
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Combined strategy + venue table */}
            <div className="overflow-x-auto">
              <table className="text-base" style={{tableLayout: 'fixed', minWidth: '600px', width: '100%'}}>
                <colgroup>
                  <col style={{width: '20%'}} />
                  <col style={{width: '20%'}} />
                  <col style={{width: '20%'}} />
                  <col style={{width: '20%'}} />
                  <col style={{width: '20%'}} />
                </colgroup>
                {/* Strategy section header */}
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-2.5 pr-4 font-semibold text-xs uppercase tracking-wider text-gray-400">By Strategy</th>
                    <th className="text-right py-2.5 px-4 font-semibold text-sm bg-blue-50/80 text-blue-700 border-l border-blue-100">Base</th>
                    <th className="text-right py-2.5 px-4 font-semibold text-sm bg-indigo-50/80 text-indigo-700 border-l border-indigo-100">Fixing</th>
                    <th className="text-right py-2.5 px-4 font-semibold text-sm bg-purple-50/80 text-purple-700 border-l border-purple-100">IDA1</th>
                    <th className="text-right py-2.5 px-4 font-bold text-sm bg-gray-100 text-gray-700 border-l border-gray-200">Total</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 pr-4 text-gray-500 font-medium text-sm">PnL (PLN)</td>
                    <td className={`text-right py-2.5 px-4 font-semibold bg-blue-50/40 border-l border-blue-50 ${results.summary.total_base_pnl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.total_base_pnl)}
                    </td>
                    <td className={`text-right py-2.5 px-4 font-semibold bg-indigo-50/40 border-l border-indigo-50 ${results.summary.total_fixing_pnl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.total_fixing_pnl)}
                    </td>
                    <td className={`text-right py-2.5 px-4 font-semibold bg-purple-50/40 border-l border-purple-50 ${(results.summary.total_ida1_pnl || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.total_ida1_pnl || 0)}
                    </td>
                    <td className={`text-right py-2.5 px-4 font-bold bg-gray-50 border-l border-gray-200 ${results.summary.total_combined_pnl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.total_combined_pnl)}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 pr-4 text-gray-500 font-medium text-sm">Volume (MWh)</td>
                    <td className="text-right py-2.5 px-4 bg-blue-50/40 border-l border-blue-50">{formatNumber(results.summary.total_base_volume_mwh || 0)}</td>
                    <td className="text-right py-2.5 px-4 bg-indigo-50/40 border-l border-indigo-50">{formatNumber(results.summary.total_arbitrage_volume_mwh || 0)}</td>
                    <td className="text-right py-2.5 px-4 bg-purple-50/40 border-l border-purple-50">{formatNumber(results.summary.total_ida1_volume_mwh || 0)}</td>
                    <td className="text-right py-2.5 px-4 font-bold bg-gray-50 border-l border-gray-200">{formatNumber(results.summary.total_volume_traded_mwh || 0)}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 pr-4 text-gray-500 font-medium text-sm">PLN/MWh</td>
                    <td className={`text-right py-2.5 px-4 bg-blue-50/40 border-l border-blue-50 ${(results.summary.profit_per_mwh_base || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.profit_per_mwh_base || 0)}
                    </td>
                    <td className={`text-right py-2.5 px-4 bg-indigo-50/40 border-l border-indigo-50 ${(results.summary.profit_per_mwh_arbitrage || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.profit_per_mwh_arbitrage || 0)}
                    </td>
                    <td className={`text-right py-2.5 px-4 bg-purple-50/40 border-l border-purple-50 ${(results.summary.profit_per_mwh_ida1 || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.profit_per_mwh_ida1 || 0)}
                    </td>
                    <td className={`text-right py-2.5 px-4 font-bold bg-gray-50 border-l border-gray-200 ${(results.summary.profit_per_mwh || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatNumber(results.summary.profit_per_mwh || 0)}
                    </td>
                  </tr>
                  {/* Venue section header */}
                  <tr className="border-b-2 border-gray-300">
                    <td className="pt-4 pb-2.5 pr-4 font-semibold text-xs uppercase tracking-wider text-gray-400">By Venue</td>
                    <td className="pt-4 pb-2.5 px-4 text-right font-semibold text-sm bg-blue-50/80 text-blue-700 border-l border-blue-100">BASE</td>
                    <td className="pt-4 pb-2.5 px-4 text-right font-semibold text-sm bg-indigo-50/80 text-indigo-700 border-l border-indigo-100">Fixing I</td>
                    <td className="pt-4 pb-2.5 px-4 text-right font-semibold text-sm bg-purple-50/80 text-purple-700 border-l border-purple-100">Fixing II</td>
                    <td className="pt-4 pb-2.5 px-4 text-right font-semibold text-sm bg-amber-50/80 text-amber-700 border-l border-amber-100">IDA1</td>
                  </tr>
                  {(() => {
                    const vBase = results.summary.venue_base_mwh || 0;
                    const vFixI = results.summary.venue_fix_i_mwh || 0;
                    const vFixII = results.summary.venue_fix_ii_mwh || 0;
                    const vIDA1 = results.summary.venue_ida1_mwh || 0;
                    const vTotal = vBase + vFixI + vFixII + vIDA1;
                    return (
                      <>
                        <tr className="border-b border-gray-100">
                          <td className="py-2.5 pr-4 text-gray-500 font-medium text-sm">Volume (MWh)</td>
                          <td className="text-right py-2.5 px-4 bg-blue-50/40 border-l border-blue-50">{formatNumber(vBase)}</td>
                          <td className="text-right py-2.5 px-4 bg-indigo-50/40 border-l border-indigo-50">{formatNumber(vFixI)}</td>
                          <td className="text-right py-2.5 px-4 bg-purple-50/40 border-l border-purple-50">{formatNumber(vFixII)}</td>
                          <td className="text-right py-2.5 px-4 bg-amber-50/40 border-l border-amber-50">{formatNumber(vIDA1)}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2.5 pr-4 text-gray-500 font-medium text-sm">Ratio (%)</td>
                          <td className="text-right py-2.5 px-4 bg-blue-50/40 border-l border-blue-50 font-medium">{vTotal > 0 ? (vBase / vTotal * 100).toFixed(1) : '0.0'}%</td>
                          <td className="text-right py-2.5 px-4 bg-indigo-50/40 border-l border-indigo-50 font-medium">{vTotal > 0 ? (vFixI / vTotal * 100).toFixed(1) : '0.0'}%</td>
                          <td className="text-right py-2.5 px-4 bg-purple-50/40 border-l border-purple-50 font-medium">{vTotal > 0 ? (vFixII / vTotal * 100).toFixed(1) : '0.0'}%</td>
                          <td className="text-right py-2.5 px-4 bg-amber-50/40 border-l border-amber-50 font-medium">{vTotal > 0 ? (vIDA1 / vTotal * 100).toFixed(1) : '0.0'}%</td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            <div className="mt-5"></div>
            {/* Bucket Success Rates */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Bucket 1 Success Rate */}
              <div className="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-lime-600 font-medium">Bucket 1 (q30/q70)</p>
                    <p className="text-2xl font-bold text-lime-800">
                      {results.summary.bucket_stats?.bucket1?.percentage || 0}%
                    </p>
                    <p className="text-xs text-lime-600">
                      {results.summary.bucket_stats?.bucket1?.profitable || 0} of {results.summary.bucket_stats?.bucket1?.total || 0} hours profitable
                    </p>
                    <div className="mt-2 pt-2 border-t border-lime-200">
                      <p className="text-xs text-lime-700 font-medium">Quantile Accuracy:</p>
                      <p className="text-xs text-lime-600">
                        q30 (buy): {results.summary.bucket_stats?.bucket1?.lower_accuracy || 0}% ({results.summary.bucket_stats?.bucket1?.price_above_lower || 0}/{results.summary.bucket_stats?.bucket1?.lower_total || 0})
                      </p>
                      <p className="text-xs text-lime-600">
                        q70 (sell): {results.summary.bucket_stats?.bucket1?.upper_accuracy || 0}% ({results.summary.bucket_stats?.bucket1?.price_below_upper || 0}/{results.summary.bucket_stats?.bucket1?.upper_total || 0})
                      </p>
                    </div>
                  </div>
                  <TrendingUp className="text-lime-500" size={32} />
                </div>
              </div>

              {/* Bucket 2 Success Rate */}
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-sky-600 font-medium">Bucket 2 (q20/q80)</p>
                    <p className="text-2xl font-bold text-sky-800">
                      {results.summary.bucket_stats?.bucket2?.percentage || 0}%
                    </p>
                    <p className="text-xs text-sky-600">
                      {results.summary.bucket_stats?.bucket2?.profitable || 0} of {results.summary.bucket_stats?.bucket2?.total || 0} hours profitable
                    </p>
                    <div className="mt-2 pt-2 border-t border-sky-200">
                      <p className="text-xs text-sky-700 font-medium">Quantile Accuracy:</p>
                      <p className="text-xs text-sky-600">
                        q20 (buy): {results.summary.bucket_stats?.bucket2?.lower_accuracy || 0}% ({results.summary.bucket_stats?.bucket2?.price_above_lower || 0}/{results.summary.bucket_stats?.bucket2?.lower_total || 0})
                      </p>
                      <p className="text-xs text-sky-600">
                        q80 (sell): {results.summary.bucket_stats?.bucket2?.upper_accuracy || 0}% ({results.summary.bucket_stats?.bucket2?.price_below_upper || 0}/{results.summary.bucket_stats?.bucket2?.upper_total || 0})
                      </p>
                    </div>
                  </div>
                  <TrendingUp className="text-sky-500" size={32} />
                </div>
              </div>

              {/* Bucket 3 Success Rate */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-pink-600 font-medium">Bucket 3 (q10/q90)</p>
                    <p className="text-2xl font-bold text-pink-800">
                      {results.summary.bucket_stats?.bucket3?.percentage || 0}%
                    </p>
                    <p className="text-xs text-pink-600">
                      {results.summary.bucket_stats?.bucket3?.profitable || 0} of {results.summary.bucket_stats?.bucket3?.total || 0} hours profitable
                    </p>
                    <div className="mt-2 pt-2 border-t border-pink-200">
                      <p className="text-xs text-pink-700 font-medium">Quantile Accuracy:</p>
                      <p className="text-xs text-pink-600">
                        q10 (buy): {results.summary.bucket_stats?.bucket3?.lower_accuracy || 0}% ({results.summary.bucket_stats?.bucket3?.price_above_lower || 0}/{results.summary.bucket_stats?.bucket3?.lower_total || 0})
                      </p>
                      <p className="text-xs text-pink-600">
                        q90 (sell): {results.summary.bucket_stats?.bucket3?.upper_accuracy || 0}% ({results.summary.bucket_stats?.bucket3?.price_below_upper || 0}/{results.summary.bucket_stats?.bucket3?.upper_total || 0})
                      </p>
                    </div>
                  </div>
                  <TrendingUp className="text-pink-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Seventh Row: Bucket Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Bucket 1 Analysis */}
              <div className="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-lime-600 font-medium">Bucket 1 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">When Right:</span>
                        <span className="text-base font-bold text-lime-800">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">When Wrong:</span>
                        <span className="text-base font-bold text-lime-800">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-lime-200">
                        <span className="text-xs text-lime-700">Volume:</span>
                        <span className="text-base font-bold text-lime-800">
                          {Math.round(results.summary.bucket_stats?.bucket1?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-lime-900">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-lime-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-lime-500" size={32} />
                </div>
              </div>

              {/* Bucket 2 Analysis */}
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-sky-600 font-medium">Bucket 2 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">When Right:</span>
                        <span className="text-base font-bold text-sky-800">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">When Wrong:</span>
                        <span className="text-base font-bold text-sky-800">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-sky-200">
                        <span className="text-xs text-sky-700">Volume:</span>
                        <span className="text-base font-bold text-sky-800">
                          {Math.round(results.summary.bucket_stats?.bucket2?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-sky-900">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-sky-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-sky-500" size={32} />
                </div>
              </div>

              {/* Bucket 3 Analysis */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-pink-600 font-medium">Bucket 3 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">When Right:</span>
                        <span className="text-base font-bold text-pink-800">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">When Wrong:</span>
                        <span className="text-base font-bold text-pink-800">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-pink-200">
                        <span className="text-xs text-pink-700">Volume:</span>
                        <span className="text-base font-bold text-pink-800">
                          {Math.round(results.summary.bucket_stats?.bucket3?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-pink-900">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-pink-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-pink-500" size={32} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Results */}
        {results && results.results && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Daily Results</h2>
            <div className="space-y-2 overflow-x-auto">
              {Object.entries(results.results)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, data]) => renderDateRow(date, data))}
            </div>
            
            {/* Hourly Performance Chart */}
            {hourlyPerformanceData.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Hourly Arbitrage Analysis</h3>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="hour" 
                        label={{ value: 'Hour', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        label={{ value: 'PnL', angle: -90, position: 'insideLeft' }}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip formatter={(value) => formatNumber(value)} />
                      <ReferenceLine y={0} stroke="#666" />
                      <Bar dataKey="pnl">
                        {hourlyPerformanceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? "#10b981" : "#ef4444"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Positive Hours</p>
                    <p className="text-xl font-bold text-green-700">
                      {hourlyPerformanceData.filter(d => d.pnl > 0).length}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Negative Hours</p>
                    <p className="text-xl font-bold text-red-700">
                      {hourlyPerformanceData.filter(d => d.pnl < 0).length}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IDA1Model;
